import { useCallback, useRef } from 'react';
import type { ChatMessage } from '../../ChatPanel';
import { logResponseStats } from '../../../utils/statsLogger';
import logger from '../../../utils/simpleLogger';
import { useBatchAccumulator } from './useBatchAccumulator';

/**
 * Hook for streaming event handlers: RAF throttling, batch accumulation,
 * message finalization, and error handling.
 */

export interface UseStreamingHandlersOptions {
  onMessagesUpdate: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  stopTyping: () => void;
  stopLoading: () => void;
  setError: (error: string | null) => void;
  handleStreamCancellation: () => void;
  onStreamingEnd?: () => void;
  onStreamingTimeout?: (isTimedOut: boolean) => void;
}

export function useStreamingHandlers(options: UseStreamingHandlersOptions) {
  const {
    onMessagesUpdate,
    stopTyping,
    stopLoading,
    setError,
    handleStreamCancellation,
    onStreamingEnd,
    onStreamingTimeout,
  } = options;

  const batchAccumulator = useBatchAccumulator();

  // RAF ref for throttling
  const rafIdRef = useRef<number | null>(null);
  const pendingTextRef = useRef<string | null>(null);

  // C3: Timeout indicator for long streaming (15s without tokens)
  const STREAMING_TIMEOUT_MS = 15000;
  const streamingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetStreamingTimeout = useCallback(() => {
    if (streamingTimeoutRef.current) clearTimeout(streamingTimeoutRef.current);
    onStreamingTimeout?.(false);
    streamingTimeoutRef.current = setTimeout(() => {
      onStreamingTimeout?.(true);
    }, STREAMING_TIMEOUT_MS);
  }, [onStreamingTimeout]);

  const clearStreamingTimeout = useCallback(() => {
    if (streamingTimeoutRef.current) clearTimeout(streamingTimeoutRef.current);
    streamingTimeoutRef.current = null;
    onStreamingTimeout?.(false);
  }, [onStreamingTimeout]);

  /**
   * Flush pending text update to UI
   */
  const flushPendingUpdate = useCallback(
    (streamingMessageId: string) => {
      if (pendingTextRef.current === null) {
        rafIdRef.current = null;
        return;
      }

      const textToUpdate = pendingTextRef.current;
      pendingTextRef.current = null;
      rafIdRef.current = null;

      const accumulatedCitations = batchAccumulator.getAccumulatedCitations();

      onMessagesUpdate((prev) => {
        return prev.map((msg) => {
          if (msg.id === streamingMessageId) {
            return {
              ...msg,
              text: textToUpdate,
              markdown: textToUpdate,
              citations: accumulatedCitations as ChatMessage['citations'],
              isStreaming: true,
            };
          }
          return msg;
        });
      });
    },
    [batchAccumulator, onMessagesUpdate]
  );

  /**
   * Schedule throttled text update via RAF
   * C3: Resets streaming timeout on every token received
   */
  const scheduleUpdate = useCallback(
    (text: string, streamingMessageId: string) => {
      pendingTextRef.current = text;
      resetStreamingTimeout();
      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(() => flushPendingUpdate(streamingMessageId));
      }
    },
    [flushPendingUpdate, resetStreamingTimeout]
  );

  /**
   * Cancel any pending RAF update
   */
  const cancelPendingRAF = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  /**
   * Finalize message with complete response
   */
  const finalizeMessage = useCallback(
    (
      tempUserMessage: ChatMessage,
      streamingMessageId: string,
      data: {
        response: string;
        citations: Record<string, unknown>;
        usageStats?: Record<string, unknown>;
        ragStats?: Record<string, unknown>;
        aiFlowTrace?: Array<{
          stage: string;
          name: string;
          time_ms: number;
          status: string;
          data?: Record<string, unknown>;
        }>;
        messageId?: string;
        userMessageId?: string;
        autoSelectionInfo?: {
          document_ids: string[];
          document_names: string[];
        };
      },
      isMapReduce: boolean = false
    ) => {
      cancelPendingRAF();
      clearStreamingTimeout();

      const finalUserMessage: ChatMessage = {
        ...tempUserMessage,
        id: data.userMessageId || tempUserMessage.id,
      };

      // Calculate RAG confidence from stats (mirrors backend calculate_rag_confidence)
      let confidence: number | undefined;
      if (data.ragStats) {
        const stats = data.ragStats as Record<string, any>;
        const totalChunks = stats.total_chunks_retrieved ?? 0;
        const relevantChunks = stats.relevant_chunks ?? totalChunks;
        const avgRelevance = stats.average_relevance ?? 0.5;
        const hasCitations = (stats.citations_count ?? 0) > 0;

        if (totalChunks === 0) {
          confidence = 0.1;
        } else {
          const relevantRatio = relevantChunks / totalChunks;
          const relevanceFactor = avgRelevance * (0.5 + 0.5 * relevantRatio);
          const citationBoost = hasCitations ? 0.2 : 0;
          confidence = Math.min(1.0, relevanceFactor + citationBoost);
        }
      }

      const finalAssistantMessage: ChatMessage = {
        id: data.messageId || `assistant-${Date.now()}`,
        text: data.response,
        isUser: false,
        timestamp: new Date(),
        markdown: data.response,
        citations: data.citations as ChatMessage['citations'],
        usageStats: data.usageStats,
        ragStats: data.ragStats,
        aiFlowTrace: data.aiFlowTrace,
        confidence,
        isStreaming: false,
        autoSelectionInfo: data.autoSelectionInfo,
      };

      logResponseStats({
        usageStats: data.usageStats,
        aiFlowTrace: data.aiFlowTrace,
        ragStats: data.ragStats,
        isMapReduce,
      });

      onMessagesUpdate((current) => {
        const otherMessages = current.filter((m) => {
          if (m.isUser && m.id === tempUserMessage.id) return false;
          if (!m.isUser && m.id === streamingMessageId) return false;
          if (
            !m.isUser &&
            (m.batchNumber !== undefined ||
              m.id?.startsWith('batch-') ||
              (m.isStreaming && m.id !== streamingMessageId))
          ) {
            return false;
          }
          return true;
        });

        const finalMessages = [...otherMessages, finalUserMessage, finalAssistantMessage];
        return Array.from(new Map(finalMessages.map((m) => [m.id, m])).values());
      });

      stopTyping();
      stopLoading();
      setTimeout(() => onStreamingEnd?.(), 0);
    },
    [
      cancelPendingRAF,
      clearStreamingTimeout,
      onMessagesUpdate,
      stopTyping,
      stopLoading,
      onStreamingEnd,
    ]
  );

  /**
   * Handle streaming error with context-aware error messages
   */
  const handleStreamError = useCallback(
    (error: Error | string | { message: string; retryable?: boolean; category?: string }) => {
      // Handle structured error from SSE
      if (typeof error === 'object' && 'message' in error && !(error instanceof Error)) {
        clearStreamingTimeout();

        const structured = error as { message: string; retryable?: boolean; category?: string };
        // Use the backend-provided message directly (it's already user-friendly)
        setError(structured.message);
        stopTyping();
        stopLoading();
        onStreamingEnd?.();
        return;
      }

      const errorStr =
        typeof error === 'string' ? error : (error as Error).message || String(error);

      clearStreamingTimeout();

      // Check for cancellation
      if (
        errorStr === 'Consulta cancelada' ||
        errorStr.includes('cancelada') ||
        (error instanceof Error && error.name === 'AbortError')
      ) {
        handleStreamCancellation();
        onStreamingEnd?.();
        return;
      }

      console.error('❌ Error en streaming:', errorStr);

      let errorMessage = 'Error al enviar el mensaje';

      const errorLower = errorStr.toLowerCase();
      if (errorLower.includes('429') || errorLower.includes('rate_limit_exceeded')) {
        errorMessage =
          '⚠️ Has enviado demasiados mensajes en poco tiempo. Espera un momento e intenta de nuevo.';
      } else if (errorLower.includes('network') || errorLower.includes('fetch')) {
        errorMessage =
          '🌐 Error de conexión. Verifica tu conexión a internet e intenta nuevamente.';
      } else if (errorLower.includes('timeout')) {
        errorMessage = '⏱️ La solicitud tardó demasiado. Intenta de nuevo.';
      } else if (errorLower.includes('401') || errorLower.includes('unauthorized')) {
        errorMessage = '🔑 Tu sesión ha expirado. Inicia sesión de nuevo.';
      } else if (errorLower.includes('api key')) {
        errorMessage = '🔑 Error de configuración del servidor. Contacta al administrador.';
      } else if (errorStr.length < 200) {
        errorMessage = `Error: ${errorStr}`;
      }

      setError(errorMessage);
      stopTyping();
      stopLoading();
      onStreamingEnd?.();
    },
    [
      clearStreamingTimeout,
      handleStreamCancellation,
      setError,
      stopTyping,
      stopLoading,
      onStreamingEnd,
    ]
  );

  return {
    batchAccumulator,
    scheduleUpdate,
    flushPendingUpdate,
    cancelPendingRAF,
    finalizeMessage,
    handleStreamError,
    resetStreamingTimeout,
    clearStreamingTimeout,
  };
}
